import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';
import { privateSelectionColor } from '@canvas/constants/colors/selection';
import { getCollaboratorSVGBackgroundColor } from '@canvas/constants/get-color-value';
import { base64Encode } from '@canvas/encoding';
import { RecoverableNetworkError, retryWithBackoff } from '@canvas/retry';

import { anonymousAssets, anonymousAssetWithoutAnimal } from './anonymous-assets.ts';

export const svgToDataUri = (svgRoot: Element) => {
  // by this point we have a safe SVG string: we can safely string interpolate to encode as a
  // data:image to enable passing it to an <img> src attribute
  return `data:image/svg+xml;base64,${base64Encode(
    new XMLSerializer().serializeToString(svgRoot),
  )}`;
};

/**
 * Adds a background color to the SVG image by adding a style attribute to the SVG element, and
 * encoding the SVG as a data:image/svg+xml URI component.
 */
export function addBackgroundColor(svgRoot: Element, bgColor: string) {
  const styleRule = new CSSStyleSheet();

  // even if bgColor is malicious, it will be sanitized by the CSSStyleSheet with the string being
  // empty, ensuring safety
  styleRule.insertRule(`svg { background-color: ${bgColor}; }`);
  // we need to cast to CSSStyleRule to access the .style property
  const styleRuleString = (styleRule.cssRules[0] as CSSStyleRule).style.cssText;

  svgRoot.setAttribute('style', styleRuleString);
}

export const getAssetIndex = (clientId: number) => clientId % anonymousAssets.length;

export const svgStringToDomDocument = (svgString: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(svgString, 'image/svg+xml');

  return doc;
};

/**
 * Gets an anonymous avatar randomly based on the client id obtained from Y.
 * Dynamically adds the correct (lighter) background to the svg image
 *
 * @param clientId The client id obtained from Y.
 * @returns The anonymous asset object with the avatar of the anonymous user (or undefined
 *  if the fetch with retry fails)
 */
export const getAnonymousAvatarUrl = async (
  clientId: number,
): Promise<string | undefined> => {
  const { emojiSvg } = anonymousAssets[getAssetIndex(clientId)];

  const response = await retryWithBackoff({
    callback: () => fetch(emojiSvg),
    maxRetries: 3,
    shouldRetry: (error) => error instanceof RecoverableNetworkError,
  });

  if (!response.ok) {
    return undefined;
  }

  const svgString = await response.text();

  const bgColor = getCollaboratorSVGBackgroundColor(clientId).toString('rgb');

  const doc = svgStringToDomDocument(svgString);

  const svg = doc.documentElement;

  addBackgroundColor(svg, bgColor);

  return svgToDataUri(svg);
};

export const getPrivateAvatarUrl = async (): Promise<string | undefined> => {
  const response = await retryWithBackoff({
    callback: () => fetch(anonymousAssetWithoutAnimal.emojiSvg),
    maxRetries: 3,
    shouldRetry: (error) => error instanceof RecoverableNetworkError,
  });

  if (!response.ok) {
    return undefined;
  }

  const svgString = await response.text();

  const doc = svgStringToDomDocument(svgString);

  const svg = doc.documentElement;

  svg.setAttribute('viewBox', '-4 -4 24 24');
  if (isFeatureFlagEnabled('canvas-add-anonymous-background')) {
    addBackgroundColor(svg, privateSelectionColor.toString('rgb'));
  }

  // Change fill color to white for all path elements
  const paths = svg.querySelectorAll('path');
  paths.forEach((path) => {
    path.setAttribute('fill', 'white');
  });

  return svgToDataUri(svg);
};

/**
 * Gets the anonymous asset name for the current user and other collaborators based on a client id obtained from Y.
 * @param clientId The client id obtained from Y.
 * @returns A message descriptor which can be used with useMessageString to get the actual name.
 */
export const getAnonymousName = (clientId: number) => {
  const { yourName, collaboratorName } = anonymousAssets[getAssetIndex(clientId)];
  return { yourName, collaboratorName };
};

/**
 * Gets the anonymous asset name without animal for the current user and other collaborators.
 * @returns A message descriptor which can be used with useMessageString to get the actual name.
 */
export const getAnonymousNameWithoutAnimal = () => {
  const { yourName, collaboratorName } = anonymousAssetWithoutAnimal;
  return { yourName, collaboratorName };
};
